import api from './api';
import practiceService from './practiceService';

export const getMeasurementErrorMessage = (error, fallbackMessage = 'Something went wrong') => {
  const code = error?.response?.data?.code;
  if (code === 'PRACTICE_LOCKED_READ_ONLY') {
    return 'Practice is locked. You can view readings only. Contact administrator.';
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

const notifyMeasurementChanged = (practiceId, patientId) => {
  if (practiceId) {
    practiceService.invalidatePracticePatientsCache(practiceId);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('patient:measurement-changed', { detail: { practiceId, patientId } }));
  }
};

const measurementService = {
  // Create blood pressure measurement
  createBloodPressureMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Get blood pressure measurements
  getBloodPressureMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      { params }
    );
    return response;
  },

  // Update blood pressure measurement
  updateBloodPressureMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/${measurementId}`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Delete blood pressure measurement
  deleteBloodPressureMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/${measurementId}`
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Get blood pressure measurements by date range
  getBloodPressureByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure`,
      {
        params: {
          fromDate,
          toDate,
          sortBy
        }
      }
    );
    return response;
  },

  // Sync measurement with ForaCare
  syncMeasurementWithForaCare: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/foracare-sync`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Blood Glucose Measurements
  createBloodGlucoseMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  getBloodGlucoseMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      { params }
    );
    return response;
  },

  getBloodGlucoseByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose`,
      {
        params: {
          fromDate,
          toDate,
          sortBy
        }
      }
    );
    return response;
  },

  updateBloodGlucoseMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/${measurementId}`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  deleteBloodGlucoseMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/${measurementId}`
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Weight Measurements
  createWeightMeasurement: async (practiceId, patientId, measurementData) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  getWeightMeasurements: async (practiceId, patientId, params = {}) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      { params }
    );
    return response;
  },

  getWeightByDateRange: async (practiceId, patientId, fromDate, toDate, sortBy = 'date') => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight`,
      {
        params: {
          fromDate,
          toDate,
          sortBy
        }
      }
    );
    return response;
  },

  updateWeightMeasurement: async (practiceId, patientId, measurementId, measurementData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight/${measurementId}`,
      measurementData
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  deleteWeightMeasurement: async (practiceId, patientId, measurementId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/measurements/weight/${measurementId}`
    );
    notifyMeasurementChanged(practiceId, patientId);
    return response;
  },

  // Clinical Analysis
  getClinicalAnalysis: async (practiceId, patientId, readings) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/analysis/blood-pressure`,
      { readings }
    );
    return response;
  }
};

export default measurementService;